import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import DashboardPeriodSelect from '../../components/dashboard/DashboardPeriodSelect';
import DashboardTeamMemberFilter from '../../components/dashboard/DashboardTeamMemberFilter';
import { AppointmentsCountCard, RevenueCard } from '../../components/dashboard/DashboardKpiCards';
import { NewCustomersCard, NewCustomerReturnRateCard, ReturnRateCard } from '../../components/dashboard/CustomerDashboardCards';
import PeakTimeCard from '../../components/dashboard/PeakTimeCard';
import {
  RecentActivityCard,
  RecentSalesCard,
  TopServicesCard,
  TopTeamMembersCard,
} from '../../components/dashboard/DashboardListCards';
import useDashboardMetric from '../../hooks/useDashboardMetric';
import usePermissions from '../../hooks/usePermissions';
import dashboardService from '../../services/dashboardService';
import TeamMemberService from '../../services/teamMemberService';
import { useAppData } from '../../context/AppDataContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Permissions } from '../../constants/Permissions';
import { DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import {
  buildTeamMemberIdsParam,
  buildTeamMemberKey,
  DASHBOARD_PERIODS,
} from '../../utils/dashboardMetricUtils';
import { getBookableTeamMembers } from '../../utils/teamMemberBookingUtils';
import { BRAND, BRAND_RADIUS } from '../../theme/brandTokens';

const PRIMARY_GRID_SX = {
  display: 'grid',
  gap: 2,
  alignItems: 'stretch',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
};

const CUSTOMER_GRID_SX = {
  display: 'grid',
  gap: 2,
  alignItems: 'stretch',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
};

const SECTION_GRID_SX = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
};

const FILTER_BAR_SX = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  gap: 2,
  alignItems: { xs: 'stretch', md: 'flex-end' },
  justifyContent: 'space-between',
  p: { xs: 2, sm: 2.5 },
  borderRadius: BRAND_RADIUS.card,
  border: `1px solid ${BRAND.line}`,
  bgcolor: BRAND.surface,
  color: BRAND.text,
  '& .MuiOutlinedInput-root': {
    bgcolor: BRAND.surface,
    color: BRAND.text,
  },
  '& .MuiSelect-icon': {
    color: BRAND.textMuted,
  },
};


const isAppAdminUser = (managementUser) => managementUser?.userType === 1
  || managementUser?.userType === '1'
  || managementUser?.userType === 'AppAdmin';

const ManagementDashboardPage = () => {
  const { t } = useTranslation();
  const { managementUser } = useAuth();
  const { showError, showSuccess } = useToast();
  const { currentStore, storesLoading, isStoreSelectionHydrated } = useAppData();
  const storeId = currentStore?.id;
  const currencyCode = currentStore?.currencyCode || DEFAULT_CURRENCY;
  const { hasPermission, hasAnyPermission, loading: permissionsLoading } = usePermissions(storeId);
  const canViewAgenda = hasAnyPermission([
    Permissions.TeamMembersAgendaRead,
    Permissions.TeamMembersAgendaReadOwn,
  ]);
  const canViewAllFinancials = hasPermission(Permissions.EmployeePaymentsRead);
  const canViewFinancials = hasAnyPermission([
    Permissions.EmployeePaymentsRead,
    Permissions.EmployeePaymentsReadOwn,
  ]);
  const canViewCustomers = hasPermission(Permissions.CustomersRead);
  const canViewInsights = canViewAgenda || canViewFinancials;
  const showRebuild = isAppAdminUser(managementUser);

  const [period, setPeriod] = useState(DASHBOARD_PERIODS.THIS_MONTH);
  const [teamMemberIds, setTeamMemberIds] = useState(null);
  const [revision, setRevision] = useState(0);
  const [rebuildConfirmOpen, setRebuildConfirmOpen] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);

  const teamMembersMetric = useDashboardMetric({
    cacheKey: `dashboard:team-members:${storeId}`,
    fetcher: () => TeamMemberService.getTeamMembersByStore(storeId),
    enabled: Boolean(storeId) && !permissionsLoading && canViewInsights,
    revision,
  });
  const bookableTeamMembers = useMemo(
    () => getBookableTeamMembers(teamMembersMetric.data),
    [teamMembersMetric.data],
  );
  const showTeamMemberFilter = bookableTeamMembers.length > 1;

  const insightsMetric = useDashboardMetric({
    cacheKey: `dashboard:insights:${storeId}:${period}:${buildTeamMemberKey(teamMemberIds)}`,
    fetcher: () => dashboardService.getDashboardInsights(storeId, {
      period,
      teamMemberIds: buildTeamMemberIdsParam(teamMemberIds),
    }),
    enabled: Boolean(storeId) && !permissionsLoading && canViewInsights,
    revision,
  });

  const recentMetric = useDashboardMetric({
    cacheKey: `dashboard:recent:${storeId}`,
    fetcher: () => dashboardService.getDashboardRecent(storeId),
    enabled: Boolean(storeId) && !permissionsLoading && (canViewAgenda || canViewFinancials),
    revision,
  });

  const insights = insightsMetric.data;
  const recent = recentMetric.data;
  const insightsPending = insightsMetric.loading && !insights;
  const insightsFailed = Boolean(insightsMetric.error);
  const showAppointments = canViewAgenda && !insightsMetric.forbidden
    && (insightsPending || insightsFailed || (insights && insights.appointmentsCount != null));
  const showRevenue = canViewAllFinancials && !insightsMetric.forbidden
    && (insightsPending || insightsFailed || (insights && insights.revenue != null));
  const showTopTeam = canViewAllFinancials && !insightsMetric.forbidden
    && (insightsPending || insightsFailed || insights?.topTeamMembers != null);
  const recentPending = recentMetric.loading && !recentMetric.data;
  const recentHidden = recentMetric.forbidden;

  const handleRebuildDashboard = async () => {
    if (!storeId || rebuilding) return;

    setRebuilding(true);
    try {
      await dashboardService.rebuildStoreDashboard(storeId);
      showSuccess(t('dashboard.rebuildSuccess'));
      setRevision((current) => current + 1);
    } catch {
      showError(t('dashboard.rebuildError'));
    } finally {
      setRebuilding(false);
      setRebuildConfirmOpen(false);
    }
  };

  if (!storeId && !storesLoading && isStoreSelectionHydrated) {
    return (
      <ManagementPageLayout pageTitle={t('dashboard.title')} pageSubtitle={t('dashboard.subtitle')}>
        <Alert severity="info">{t('dashboard.selectStore')}</Alert>
      </ManagementPageLayout>
    );
  }

  if (storesLoading || !isStoreSelectionHydrated || permissionsLoading) {
    return (
      <ManagementPageSkeleton pageTitle={t('dashboard.title')} pageSubtitle={t('dashboard.subtitle')} />
    );
  }

  return (
    <ManagementPageLayout pageTitle={t('dashboard.title')} pageSubtitle={t('dashboard.subtitle')}>
      <Stack spacing={2.5} sx={{ minWidth: 0 }}>
        {(canViewInsights || showRebuild) && (
          <Box sx={FILTER_BAR_SX}>
            {canViewInsights && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
                {showTeamMemberFilter && (
                  <Box sx={{ minWidth: { sm: 240 } }}>
                    <DashboardTeamMemberFilter
                      label={t('dashboard.filters.professionals')}
                      teamMembers={bookableTeamMembers}
                      value={teamMemberIds}
                      onChange={setTeamMemberIds}
                    />
                  </Box>
                )}
                <Box sx={{ minWidth: { sm: 200 } }}>
                  <DashboardPeriodSelect
                    label={t('dashboard.filters.period')}
                    value={period}
                    onChange={setPeriod}
                  />
                </Box>
              </Stack>
            )}

            {showRebuild && (
              <Button
                variant="contained"
                disableElevation
                disabled={rebuilding}
                onClick={() => setRebuildConfirmOpen(true)}
                sx={{
                  alignSelf: { xs: 'stretch', md: 'auto' },
                  textTransform: 'none',
                  borderRadius: BRAND_RADIUS.button,
                  bgcolor: BRAND.primary,
                  color: BRAND.onPrimary,
                  '&:hover': { bgcolor: BRAND.primaryDark },
                }}
              >
                {t('dashboard.rebuild')}
              </Button>
            )}
          </Box>
        )}

        {(showAppointments || showRevenue) && (
          <Box sx={PRIMARY_GRID_SX}>
            {showAppointments && (
              <AppointmentsCountCard
                insights={insights}
                isPending={insightsPending}
                hasError={insightsFailed}
                onRetry={() => setRevision((current) => current + 1)}
              />
            )}
            {showRevenue && (
              <RevenueCard
                insights={insights}
                currencyCode={currencyCode}
                isPending={insightsPending}
                hasError={insightsFailed}
                onRetry={() => setRevision((current) => current + 1)}
              />
            )}
          </Box>
        )}

        {canViewCustomers && (
          <Box sx={CUSTOMER_GRID_SX}>
            <NewCustomersCard storeId={storeId} />
            <ReturnRateCard storeId={storeId} />
            <NewCustomerReturnRateCard storeId={storeId} />
          </Box>
        )}

        {canViewAgenda && !insightsMetric.forbidden && (
          <PeakTimeCard storeId={storeId} teamMemberIds={teamMemberIds} />
        )}

        <Box sx={SECTION_GRID_SX}>
          {showAppointments && (
            <TopServicesCard
              services={insights?.topServices ?? []}
              isLoading={insightsPending}
              hasError={insightsFailed}
              onRetry={() => setRevision((current) => current + 1)}
            />
          )}

          {showTopTeam && (
            <TopTeamMembersCard
              members={insights?.topTeamMembers ?? []}
              currencyCode={currencyCode}
              isLoading={insightsPending}
              hasError={insightsFailed}
              onRetry={() => setRevision((current) => current + 1)}
            />
          )}

          {(canViewAgenda || canViewFinancials) && !recentHidden && (
            <RecentActivityCard
              activities={recent?.activities ?? []}
              currencyCode={currencyCode}
              isLoading={recentPending}
              hasError={Boolean(recentMetric.error)}
              onRetry={() => setRevision((current) => current + 1)}
            />
          )}

          {canViewFinancials && !recentHidden && (
            <RecentSalesCard
              sales={recent?.recentSales ?? []}
              currencyCode={currencyCode}
              isLoading={recentPending}
              hasError={Boolean(recentMetric.error)}
              onRetry={() => setRevision((current) => current + 1)}
            />
          )}
        </Box>

        <ConfirmDialog
          open={rebuildConfirmOpen}
          onClose={() => setRebuildConfirmOpen(false)}
          onConfirm={handleRebuildDashboard}
          title={t('dashboard.rebuildConfirmTitle')}
          message={t('dashboard.rebuildConfirmMessage')}
          confirmText={t('dashboard.rebuild')}
          loading={rebuilding}
        />
      </Stack>
    </ManagementPageLayout>
  );
};

export default ManagementDashboardPage;
